import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAntdAppMessage } from '../../hooks/useAntdAppMessage';
import { useAuth } from '../../hooks/useAuth';
import { useSecureService } from '../../store/service';
import footerStyles from './FooterActions.module.scss';
import StatusChangeModal from '../../modals/statusChangeModal';
import ApprovalModal from '../../modals/approvalModal';
import DeleteModal from '../../components/DeleteModal/DeleteModal';
import ShareModal from '../../components/ShareModal/ShareModal';
import CompareModal from '../../components/CompareModal/CompareModal';
import AddNewFileModal from '../../modals/addNewFileModal';
import AddNewFolderModal from '../../modals/addNewFolderModal';
import AddNewProjectModal from '../../modals/addNewProjectModal';
import StatusSelector from '../../components/StatusSelector/StatusSelector';
import ActionButtons from '../../components/ActionButtons/ActionButtons';
import { flattenCommentsWithNestedReplies } from '../../utils/flattenVersionComments';
import { Role, hasRole, ROLES_PROJECT_ORG_ADMIN, ROLES_CONTENT_EDITOR, canChangeEntryStatus } from '../../constants/roles';

interface FooterActionsProps {
  onCompare?: () => void;
  onDelete?: () => void;
  onDownload?: () => void;
  onMove?: () => void;
  onDuplicate?: () => void;
  /** Move/Duplicate butonlarını footer'da göster (proje/klasör çoklu seçim) */
  showMoveDuplicate?: boolean;
  /** Move/Duplicate için yetki */
  canMoveOrDuplicate?: boolean;
  onCopyUrl?: () => void;
  onStatusChange?: () => void;
  onApprovalRequest?: () => void; // Approval modal'ı açma fonksiyonu
  fileDetail?: any;
  selectedItems?: Set<number>;
  selectedItemsDetails?: Array<{ id: number; name: string; entryType: string; status?: string; share?: any[]; thumbnail?: any }>;
  currentEntry?: any;
  style?: React.CSSProperties;
  selectedVersion?: number; // Mevcut seçili versiyon
  hideDownload?: boolean; // Download butonunu gizleme seçeneği
  isHeaderless?: boolean; // Headerless layout kullanımı için
  /** Sağ tık / ... menüsünden Share seçildiğinde parent true yapar; modal açılır */
  openShareModal?: boolean;
  onShareModalOpenChange?: (open: boolean) => void;
  /** Sağ tık / ... menüsünden Delete seçildiğinde parent true yapar; delete modal açılır */
  openDeleteModal?: boolean;
  onDeleteModalOpenChange?: (open: boolean) => void;
  /** Menüden Assign/Delete açıldığında alt aksiyon şeridini gösterme; yalnızca modallar */
  modalsOnly?: boolean;
  /** Assign veya Delete modalı kapandığında (iptal veya başarı); menüden açılan hedef state’ini temizlemek için */
  onShareOrDeleteModalClosed?: () => void;
  /** External projects/folders için mor modal teması */
  variant?: 'internal' | 'external';
  /** File detail üst çubuğu: sabit alt footer yerine inline şerit */
  barLayout?: 'default' | 'playerTopBar';
  /** File detail oynatıcı: Compare yalnızca video için (playerTopBar + bu prop verildiğinde uygulanır) */
  fileViewerMediaKind?: 'video' | 'image' | 'pdf' | 'other';
  /** File detail video compare açıkken Compare tıkı: modal yerine çıkış */
  compareModeActive?: boolean;
  onCompareExit?: () => void;
}

const FooterActions: React.FC<FooterActionsProps> = ({
  onCompare,
  onDelete,
  onDownload,
  onMove,
  onDuplicate,
  showMoveDuplicate = false,
  canMoveOrDuplicate = false,
  onCopyUrl,
  onStatusChange,
  onApprovalRequest,
  fileDetail,
  selectedItems,
  selectedItemsDetails,
  currentEntry,
  style,
  selectedVersion,
  hideDownload,
  isHeaderless,
  openShareModal,
  onShareModalOpenChange,
  openDeleteModal,
  onDeleteModalOpenChange,
  modalsOnly = false,
  onShareOrDeleteModalClosed,
  variant = 'internal',
  barLayout = 'default',
  fileViewerMediaKind,
  compareModeActive,
  onCompareExit,
}) => {
  const message = useAntdAppMessage();
  const { user, canDelete } = useAuth();
  
  // Guest ve Viewer (salt okunur istemci) için paylaşım kapalı
  const canShare = (): boolean => {
    if (!user) return false;
    return user.role !== Role.Guest && user.role !== Role.Viewer;
  };

  // Edit yetkisi: dosya /api/File/edit — Admin, SuperAdmin, Editor; klasör·proje — Admin, SuperAdmin
  const canEdit = (): boolean => {
    if (window.location.pathname.includes('/versions') ||
        window.location.pathname.includes('/file/') ||
        window.location.pathname.includes('/file-detail/') ||
        fileDetail) {
      return false;
    }
    if (!user) return false;
    const first = selectedItemsDetails?.[0];
    if (first?.entryType === 'File') {
      return hasRole(user.role, ...ROLES_CONTENT_EDITOR);
    }
    return hasRole(user.role, ...ROLES_PROJECT_ORG_ADMIN);
  };

  // Toplu seçim — Admin, SuperAdmin (proje yönetimi ile hizalı)
  const canMultiSelect = () => {
    if (!user) return false;
    return hasRole(user.role, ...ROLES_PROJECT_ORG_ADMIN);
  };

  /** Kart ... menüsündeki download ile aynı: dosya, klasör, proje (path ile); FileDetail'da versiyon URL'i */
  const itemIsDownloadable = (item: any): boolean => {
    if (!item) return false;
    const t = item.entryType;
    if (t === 'File' || t === 2 || String(t) === 'File') return true;
    if (t === 'Folder' || String(t) === 'Folder') return true;
    if (t === 'Project' || String(t) === 'Project') return true;
    if (t === 'Version' || String(t) === 'Version') {
      const v = fileDetail?.versions?.find((x: any) => x.id === item.id);
      return !!(v && (v.fileUrl || v.url || v.downloadUrl || v.path));
    }
    return false;
  };

  const canDownload = (): boolean => {
    if (!selectedItems || selectedItems.size === 0) {
      return false;
    }

    if (window.location.pathname.includes('/versions')) {
      return true;
    }

    if (fileDetail && selectedVersion) {
      const selectedVersionData = fileDetail.versions?.find((v: any) => v.id === selectedVersion);
      if (
        selectedVersionData &&
        (selectedVersionData.fileUrl || selectedVersionData.url || selectedVersionData.downloadUrl || selectedVersionData.path)
      ) {
        return true;
      }
    }

    return selectedItemsDetails?.some(itemIsDownloadable) ?? false;
  };

  const { getFileById, changeStatus, editFile, editFolder, editProject } = useSecureService();
  const [, setSelectedFileDetails] = useState<any>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [selectedStatusForModal, setSelectedStatusForModal] = useState<string>('NeedsReview');
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  
  // Share modal state
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);

  // Sağ tık / ... menüsünden Share tetiklendiğinde modalı aç
  useEffect(() => {
    if (openShareModal) {
      setIsShareModalVisible(true);
      onShareModalOpenChange?.(false);
    }
  }, [openShareModal, onShareModalOpenChange]);

  // Sağ tık / ... menüsünden Delete tetiklendiğinde modalı aç
  useEffect(() => {
    if (openDeleteModal) {
      setIsDeleteModalVisible(true);
      onDeleteModalOpenChange?.(false);
    }
  }, [openDeleteModal, onDeleteModalOpenChange]);

  // Compare modal state'leri
  const [isCompareModalVisible, setIsCompareModalVisible] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);

  // Edit modal state'leri
  const [isEditFileModalVisible, setIsEditFileModalVisible] = useState(false);
  const [isEditFolderModalVisible, setIsEditFolderModalVisible] = useState(false);
  const [isEditProjectModalVisible, setIsEditProjectModalVisible] = useState(false);
  const [, setEditLoading] = useState(false);

  // Compare yetkisi kontrolü - ProjectDetail için fileDetail null olabilir
  const canCompare = () => {
    // Seçili öğe yoksa false döndür (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      return false;
    }
    
    if (!fileDetail) {
      return false; // ProjectDetail'da compare yok
    }
    
    // FileVersions sayfasında compare gösterme
    if (window.location.pathname.includes('/versions')) {
      return false;
    }

    if (
      barLayout === 'playerTopBar' &&
      fileViewerMediaKind !== undefined &&
      fileViewerMediaKind !== 'video'
    ) {
      return false;
    }
    
    if (!fileDetail?.versions || fileDetail.versions.length <= 1) { 
      return false; // Single version
    }
    
    if (!user) { 
      return false; // Anonymous users
    }
    
    if (user.role === 'Guest') { 
      // Guest users can compare - temporarily allow all
      return true;
    }
    
    return hasRole(user.role, ...ROLES_CONTENT_EDITOR, Role.Commenter, Role.Viewer);
  };

  // Compare modal açma fonksiyonu
  const handleCompareClick = () => {
    if (compareModeActive && onCompareExit) {
      onCompareExit();
      return;
    }
    // Seçili öğe yoksa işlem yapma (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    setIsCompareModalVisible(true);
  };

  // Compare modal onaylama
  const handleCompareConfirm = (selectedVersionId: number) => {
    setCompareLoading(true);
    
    // Global state'e seçilen versiyonu kaydet
    (window as any).selectedVersionForCompare = selectedVersionId;
    
    // Compare callback'ini çağır
    if (onCompare) {
      onCompare();
    }
    
    // Modal'ı kapat ve loading'i durdur
    setIsCompareModalVisible(false);
    setCompareLoading(false);
  };

  // Compare modal iptal
  const handleCompareCancel = () => {
    setIsCompareModalVisible(false);
  };

  const handleDeleteClick = () => {
    // Seçili öğe yoksa işlem yapma (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    if (!canDelete) {
      message.error('You do not have permission for this action.');
      return;
    }
    setIsDeleteModalVisible(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEditClick = () => {
    // Seçili öğe yoksa işlem yapma
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    if (!canEdit()) {
      message.error('You do not have permission for this action.');
      return;
    }



    // Seçili öğenin tipine göre ilgili modal'ı aç
    const firstSelectedItem = selectedItemsDetails?.[0];
    if (firstSelectedItem) {
      if (firstSelectedItem.entryType === 'File') {
        setIsEditFileModalVisible(true);
      } else if (firstSelectedItem.entryType === 'Folder') {
        setIsEditFolderModalVisible(true);
      } else if (firstSelectedItem.entryType === 'Project') {
        setIsEditProjectModalVisible(true);
      }
    }
  };



  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      if (onDelete) {
        await onDelete();
      }
      setIsDeleteModalVisible(false);
      onDeleteModalOpenChange?.(false);
      onShareOrDeleteModalClosed?.();
      // Success mesajı onDelete fonksiyonunda handle edilecek
    } catch (error) {
      message.error('An error occurred during deletion.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    onDeleteModalOpenChange?.(false);
    onShareOrDeleteModalClosed?.();
  };

  // Statü değiştirme — POST /api/Entry/change-status: Admin, SuperAdmin, Editor
  const canChangeStatus = () => {
    // Seçili öğe yoksa false döndür (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      return false;
    }
    
    if (!user) return false;
    
    return canChangeEntryStatus(user.role);
  };

  // Seçili dosyalar için fileDetail yükle
  const loadSelectedFileDetails = async () => {
    // FileDetail sayfasında zaten fileDetail prop'u var, gereksiz API çağrısı yapma
    if (fileDetail) {
      setSelectedFileDetails(fileDetail);
      return;
    }
    
    if (selectedItems && selectedItems.size > 0 && selectedItemsDetails) {
      const fileItems = selectedItemsDetails.filter((item: any) => item.entryType === 'File');
      if (fileItems.length > 0) {
        try {
          const fileId = fileItems[0].id; // İlk dosyayı al
          const response = await getFileById(fileId);
          if (response.success) {
            setSelectedFileDetails(response.data);
          }
        } catch (error) {
          // Error loading selected file details
        }
      }
    }
  };

  // Seçili öğeler değiştiğinde fileDetail yükle
  useEffect(() => {
    loadSelectedFileDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadSelectedFileDetails intentionally not in deps
  }, [selectedItems, selectedItemsDetails, fileDetail]);

  // Brand rolü için onay süreci
    const handleApprovalProcess = (newStatus: string, unresolvedComments: number) => {
    if (newStatus === 'Approved') {
      // Approval modal'ını aç
      setIsApprovalModalVisible(true);
    } else {
      // Updated için direkt statü değiştir
      performStatusChange('Updated', false);
    }
  };

  const handleApprovalConfirm = async (changeSubentries: boolean) => {
    setApprovalLoading(true);
    try {
      await performStatusChange('Approved', changeSubentries);
      setIsApprovalModalVisible(false);
    } catch (error) {
      message.error('Failed to change status. Please try again.');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApprovalCancel = () => {
    setIsApprovalModalVisible(false);
  };

    const handleStatusConfirm = async (newStatus: string, changeSubentries: boolean) => {
    // Normal statü değişikliği (Brand rolü artık buraya gelmez)
    await performStatusChange(newStatus, changeSubentries);
  };

  // Statü değişikliği işlemini ayrı fonksiyon olarak çıkar
  const performStatusChange = async (newStatus: string, changeSubentries: boolean) => {
    setStatusLoading(true);
    try {
      const entries = [];
      
      if (selectedItems && selectedItems.size > 0) {
        // Seçili öğelerin statüsünü değiştir
        selectedItemsDetails?.forEach((item: any) => {
          entries.push({
            id: item.id,
            entryType: item.entryType,
            entryStatus: newStatus,
            changeSubentries: changeSubentries
          });
        });
      } else if (fileDetail) {
        // FileDetail sayfasında selectedVersion ID'sini kullan
        const versionId = selectedVersion || fileDetail.id;
        entries.push({
          id: versionId,
          entryType: selectedVersion ? 'Version' : (fileDetail.entryType || 'File'),
          entryStatus: newStatus,
          changeSubentries: changeSubentries
        });
      } else if (currentEntry) {
        // Klasör/Proje statüsünü değiştir
        entries.push({
          id: currentEntry.id,
          entryType: currentEntry.entryType,
          entryStatus: newStatus,
          changeSubentries: changeSubentries
        });
      }

      if (entries.length > 0) {
        const response = await changeStatus(entries);
        if (response.success) {
          message.success('Status changed successfully.');
          setIsStatusModalVisible(false);
          // Callback ile parent component'i bilgilendir
          if (onStatusChange) {
            onStatusChange();
          }
        } else {
          // Unauthorized hatası için özel mesaj
          if (response.message === 'Unauthorized') {
            message.error('You do not have permission to change status for these items.');
          } else {
            throw new Error(response.message || 'Status change failed');
          }
        }
      }
    } catch (error) {
      // Network hatası veya diğer hatalar için genel mesaj
      message.error('Network error or insufficient permissions. Please try again.');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleStatusCancel = () => {
    setIsStatusModalVisible(false);
  };

  // Share functions
  const handleShareClick = () => {
    // Seçili öğe yoksa işlem yapma (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    setIsShareModalVisible(true);
  };

  const handleShareConfirm = async (shareList: string[]) => {
    setShareLoading(true);
    try {
      let result;
      
      if (selectedItems && selectedItems.size > 0) {
        // Update share for selected items - currently only first item
        const firstSelectedItem = selectedItemsDetails?.[0];
        if (firstSelectedItem) {
          const editData = {
            id: firstSelectedItem.id,
            name: firstSelectedItem.name,
            thumbnail: firstSelectedItem.thumbnail || null,
            share: shareList
          };
          
          if (firstSelectedItem.entryType === 'File') {
            result = await editFile(editData);
          } else if (firstSelectedItem.entryType === 'Folder') {
            result = await editFolder(editData);
          } else if (firstSelectedItem.entryType === 'Project') {
            result = await editProject(editData);
          } else if (firstSelectedItem.entryType === 'Version') {
            // Versiyon paylaşımları dosya seviyesinde yönetilir; mümkünse fileDetail.id ile güncelle
            const fileId = fileDetail?.id || firstSelectedItem.id;
            const fileName = fileDetail?.name || firstSelectedItem.name;
            const fileThumb = (fileDetail?.thumbnail || firstSelectedItem.thumbnail) || null;
            result = await editFile({ id: fileId, name: fileName, thumbnail: fileThumb, share: shareList });
          }
        }
      } else if (fileDetail) {
        // Update file share
        const editData = {
          id: fileDetail.id,
          name: fileDetail.name,
          thumbnail: fileDetail.thumbnail || null,
          share: shareList
        };
        result = await editFile(editData);
      } else if (currentEntry) {
        // Update folder/project share
        const editData = {
          id: currentEntry.id,
          name: currentEntry.name,
          thumbnail: currentEntry.thumbnail || null,
          share: shareList
        };
        
        if (currentEntry.entryType === 'Folder') {
          result = await editFolder(editData);
        } else if (currentEntry.entryType === 'Project') {
          result = await editProject(editData);
        }
      }

      if (result && result.success) {
        message.success('Share list updated successfully.');
        setIsShareModalVisible(false);
        onShareModalOpenChange?.(false);
        onShareOrDeleteModalClosed?.();
        // Notify parent component
        if (onStatusChange) {
          onStatusChange();
        }
      } else {
        throw new Error(result?.message || 'Share update failed');
      }
    } catch (error) {
      message.error('An error occurred while updating share.');
    } finally {
      setShareLoading(false);
    }
  };

  const handleShareCancel = () => {
    setIsShareModalVisible(false);
    onShareModalOpenChange?.(false);
    onShareOrDeleteModalClosed?.();
  };

  const handleDownloadClick = async () => {
    // Seçili öğe yoksa işlem yapma (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    if (!onDownload) {
      message.error('Download function not available.');
      return;
    }
    
    setDownloadLoading(true);
    try {
      await onDownload();
    } catch (error) {
      message.error('An error occurred during download.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleMoveClick = () => {
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    onMove?.();
  };

  const handleDuplicateClick = () => {
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    onDuplicate?.();
  };

  const handleStatusChange = async (newStatus: string) => {
    // Seçili öğe yoksa işlem yapma (Projects, FileVersions, ProjectDetail için)
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    if (!canChangeStatus()) {
      message.error('You do not have permission for this action.');
      return;
    }

    // Approved seçen tüm roller için özel kontrol - her zaman modal aç
    if (newStatus === 'Approved') {
      // FileDetail sayfasında mıyız kontrol et
      const isFilePage = fileDetail || (selectedItemsDetails && selectedItemsDetails.length > 0 && selectedItemsDetails[0]?.entryType === 'Version');
      
      if (isFilePage) {
        // Approved seçildiğinde resolve kontrolü yap
        const unresolvedComments = countUnresolvedComments();
        handleApprovalProcess(newStatus, unresolvedComments);
        return;
      }
      
      // ProjectDetail sayfasında da approval modal'ı aç (sadece fileDetail yoksa)
      if (currentEntry && !fileDetail && onApprovalRequest) {
        onApprovalRequest(); // ProjectDetail'ın approval modal'ını aç
        return;
      }
      
      // Projects sayfasında da approval modal'ı aç (selectedItemsDetails varsa ve onApprovalRequest varsa)
      if (selectedItemsDetails && selectedItemsDetails.length > 0 && onApprovalRequest && !fileDetail && !currentEntry) {
        onApprovalRequest(); // Projects'ın approval modal'ını aç
        return;
      }
    }

    // Diğer roller için normal modal
    setSelectedStatusForModal(newStatus);
    setIsStatusModalVisible(true);
  };

  // Resolve edilmemiş comment sayısını hesapla
  const countUnresolvedComments = () => {
    let unresolvedComments = 0;
    
    if (fileDetail?.versions) {
      fileDetail.versions.forEach((version: any) => {
        flattenCommentsWithNestedReplies(version.comments).forEach((comment: any) => {
          if (!comment.isResolved) {
            unresolvedComments++;
          }
        });
      });
    }
    
    return unresolvedComments;
  };

  const barClassName =
    barLayout === 'playerTopBar'
      ? footerStyles.playerTopBarActions
      : isHeaderless
        ? footerStyles.headerlessFooterActions
        : footerStyles.footerActions;

  const footerBarClassName =
    barLayout === 'playerTopBar'
      ? barClassName
      : [barClassName, variant === 'external' ? footerStyles.footerActionsExternal : '']
          .filter(Boolean)
          .join(' ');

  const footerBar = (
    <div
      className={footerBarClassName}
      style={{
        ...style,
        justifyContent: 'flex-end',
      }}
    >
      <div className={footerStyles.footerActionsGroup}>
        {onStatusChange && (
          <StatusSelector
            onChange={handleStatusChange}
            disabled={!selectedItems || selectedItems.size === 0}
            userRole={user?.role}
            selectedItems={selectedItems}
            selectedItemsDetails={selectedItemsDetails}
            fileDetail={fileDetail}
            currentEntry={currentEntry}
            selectedVersion={selectedVersion}
            canMultiSelect={canMultiSelect}
          />
        )}

        <ActionButtons
        canDelete={canDelete}
        canShare={canShare}
        canDownload={canDownload}
        canCompare={canCompare}
        canMultiSelect={canMultiSelect}
        selectedItems={selectedItems}
        fileDetail={fileDetail}
        currentEntry={currentEntry}
        onDelete={handleDeleteClick}
        onCompare={handleCompareClick}
        onCopyUrl={onCopyUrl}
        onDownload={handleDownloadClick}
        onShare={handleShareClick}
        onMove={handleMoveClick}
        onDuplicate={handleDuplicateClick}
        hideDownload={hideDownload}
        downloadLoading={downloadLoading}
        showShare={barLayout === 'playerTopBar'}
        showMoveDuplicate={showMoveDuplicate}
        canMoveOrDuplicate={canMoveOrDuplicate}
        />
      </div>
    </div>
  );

  const renderBarInline = isHeaderless || barLayout === 'playerTopBar';

  return (
    <>
      {!modalsOnly &&
        (renderBarInline
          ? footerBar
          : typeof document !== 'undefined'
            ? createPortal(footerBar, document.body)
            : footerBar)}

      <DeleteModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleteLoading}
        selectedItems={selectedItems}
        selectedItemsDetails={selectedItemsDetails}
        fileDetail={fileDetail}
        currentEntry={currentEntry}
        variant={variant}
      />

      {/* Status Change Modal */}
      <StatusChangeModal
        visible={isStatusModalVisible}
        onCancel={handleStatusCancel}
        onConfirm={handleStatusConfirm}
        loading={statusLoading}
        selectedStatus={selectedStatusForModal}
        selectedItemsDetails={
          selectedItems && selectedItems.size > 0 ? selectedItemsDetails : undefined
        }
        variant={variant}
      />

      <ApprovalModal
        visible={isApprovalModalVisible}
        onCancel={handleApprovalCancel}
        onConfirm={handleApprovalConfirm}
        loading={approvalLoading}
        variant={variant}
      />

      {/* Share Modal */}
      <ShareModal
        visible={isShareModalVisible}
        onCancel={handleShareCancel}
        onConfirm={handleShareConfirm}
        loading={shareLoading}
        selectedItems={selectedItems}
        selectedItemsDetails={selectedItemsDetails}
        fileDetail={fileDetail}
        currentEntry={currentEntry}
        variant={variant}
      />

      {/* Compare Modal */}
      <CompareModal
        visible={isCompareModalVisible}
        onCancel={handleCompareCancel}
        onConfirm={handleCompareConfirm}
        loading={compareLoading}
        fileDetail={fileDetail}
        selectedVersion={selectedVersion}
      />

      {/* Edit File Modal */}
      <AddNewFileModal
        isOpen={isEditFileModalVisible}
        onClose={() => setIsEditFileModalVisible(false)}
        onSuccess={() => {
          setIsEditFileModalVisible(false);
          if (onStatusChange) {
            onStatusChange();
          }
        }}
        isFromFileDetail={false}
        editMode={true}
        variant={variant}
        editData={(() => {
          // Seçili versiyonun bilgilerini al
          const selectedItem = selectedItemsDetails?.[0];
          if (selectedItem) {
            return {
              ...selectedItem,
              share: selectedItem.share || []
            };
          }
          // Fallback olarak fileDetail kullan
          return {
            ...(fileDetail || {}),
            share: fileDetail?.share || []
          };
        })()}
      />

      {/* Edit Folder Modal */}
      <AddNewFolderModal
        isOpen={isEditFolderModalVisible}
        onClose={() => setIsEditFolderModalVisible(false)}
        onSuccess={() => {
          setIsEditFolderModalVisible(false);
          if (onStatusChange) {
            onStatusChange();
          }
        }}
        editMode={true}
        variant={variant}
        editData={{
          ...(currentEntry || selectedItemsDetails?.[0]),
          share: currentEntry?.share || selectedItemsDetails?.[0]?.share || []
        }}
      />

      {/* Edit Project Modal */}
      <AddNewProjectModal
        isOpen={isEditProjectModalVisible}
        onClose={() => setIsEditProjectModalVisible(false)}
        onSuccess={() => {
          setIsEditProjectModalVisible(false);
          if (onStatusChange) {
            onStatusChange();
          }
        }}
        editMode={true}
        editData={{
          ...(currentEntry || selectedItemsDetails?.[0]),
          share: currentEntry?.share || selectedItemsDetails?.[0]?.share || []
        }}
        variant={variant}
      />
    </>
  );
};

export default FooterActions; 